import * as Select from "@radix-ui/react-select";
import { useState } from "react";
import * as rpc from "@1games-ru/rage-rpc";
import { Button, Card } from "@radix-ui/themes";
// Подключение RPC
import eventList from "@/domains/dev/eventList/eventList.ts";

function EventListContainer() {
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>("");
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [response, setResponse] = useState<[] | object>({});

  // Выбор категории
  const handleCategorySelect = (value: string) => {
    setSelectedCategory(value);
    setSelectedSubCategory(""); // Сброс подкатегории при изменении категории
    setFormData({}); // Сброс данных формы
  };

  // Выбор подкатегории
  const handleSubCategorySelect = (value: string) => {
    setSelectedSubCategory(value);
    setFormData({}); // Сброс данных формы при изменении подкатегории
  };

  // Изменение значения в поле формы
  const handleInputChange = (key: string, value: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
    console.log("Form Data Updated: ", { [key]: value }); // Логирование для проверки обновления данных
  };

  // Рендеринг поля формы в зависимости от типа данных
  const renderInputField = (key: string, value: any) => {
    if (typeof value === "boolean") {
      return (
        <div key={key} className="mb-2">
          <label className="block mb-1 text-sm font-medium text-gray-700">{key}:</label>
          <select
            className="border border-gray-300 rounded p-1 w-full"
            value={formData[key] ?? value}
            onChange={(e) => handleInputChange(key, e.target.value === "true")}
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>
      );
    }
    if (typeof value === "number") {
      return (
        <div key={key} className="mb-2">
          <label className="block mb-1 text-sm font-medium text-gray-700">{key}:</label>
          <input
            type="number"
            className="border border-gray-300 rounded p-1 w-full"
            value={formData[key] ?? value}
            onChange={(e) => handleInputChange(key, Number(e.target.value))}
          />
        </div>
      );
    }
    if (typeof value === "string") {
      return (
        <div key={key} className="mb-2">
          <label className="block mb-1 text-sm font-medium text-gray-700">{key}:</label>
          <input
            type="text"
            className="border border-gray-300 rounded p-1 w-full"
            value={formData[key] ?? value}
            onChange={(e) => handleInputChange(key, e.target.value)}
          />
        </div>
      );
    }

    return (
      <div key={key} className="mb-2">
        <label className="block mb-1 text-sm font-medium text-gray-700">{key}:</label>
        <input
          type="text"
          className="border border-gray-300 rounded p-1 w-full"
          value={formData[key] ?? value}
          onChange={(e) => handleInputChange(key, e.target.value)}
        />
      </div>
    );
  };

  const stringifyResponse = (): string => JSON.stringify(response, null, 4);

  const call = (target: any, name: string, value: any): void => {
    let targetValue = value;

    try {
      targetValue = JSON.parse(value);
    } catch (e) {
      targetValue = value;
    }

    if (typeof targetValue === "object") {
      for (const key in targetValue) {
        try {
          targetValue[key] = JSON.parse(targetValue[key]);
        } catch (e) {
          console.error(`Ошибка при преобразовании ключа ${key}:`, e);
        }
      }
    }

    target(name, targetValue)
      .then((res: any): void => {
        console.log({ res });
        setResponse(res);
      })
      .catch((e: any): void => {
        console.log({ e });
        setResponse([JSON.stringify(e)]);
      });
  };

  // Отправка данных формы
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault(); // Чтобы форма не перезагружала страницу
    if (selectedCategory && selectedSubCategory) {
      const rpcKey = eventList[selectedCategory][selectedSubCategory].name; // Получаем ключ для RPC
      console.log("RPC Call Key: ", rpcKey); // Проверяем RPC ключ
      const defaultData = eventList[selectedCategory][selectedSubCategory].dto; // Get default data
      const dataToSend = { ...defaultData, ...formData }; // Merge default data with formData
      console.log("Form Data to Send: ", dataToSend); // Проверяем данные перед отправкой
      call(rpc.callServer, rpcKey, dataToSend);
    }
  };

  return (
    <Card className="fixed bottom-10 right-10 p-5 w-[24rem] shadow-lg rounded-xl bg-gray-800">
      {/* Категории */}
      <Select.Root value={selectedCategory} onValueChange={handleCategorySelect}>
        <Select.Trigger className="mb-4 border border-gray-300 rounded p-2 w-full flex justify-between items-center">
          <Select.Value placeholder="Выберите категорию" />
          <Select.Icon />
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className="border border-gray-300 rounded shadow-lg bg-gray-800">
            <Select.Viewport className="bg-gray-800">
              {Object.entries(eventList).map(([categoryKey]) => (
                <Select.Item key={categoryKey} value={categoryKey} className="p-2 cursor-pointer">
                  <Select.ItemText>
                    {categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1)}
                  </Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>

      {/* Подкатегории */}
      {selectedCategory && (
        <Select.Root value={selectedSubCategory} onValueChange={handleSubCategorySelect}>
          <Select.Trigger className="mb-4 border border-gray-300 rounded p-2 w-full flex justify-between items-center">
            <Select.Value placeholder="Выберите подкатегорию" />
            <Select.Icon />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="border border-gray-300 rounded shadow-lg bg-gray-800">
              <Select.Viewport className="bg-gray-800">
                {Object.entries(eventList[selectedCategory]).map(
                  ([subCategoryKey, subCategoryVal]) => (
                    <Select.Item
                      key={subCategoryKey}
                      value={subCategoryKey}
                      className="p-2  cursor-pointer"
                    >
                      <Select.ItemText>{subCategoryVal.name}</Select.ItemText>
                    </Select.Item>
                  ),
                )}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      )}

      {/* Форма на основе DTO */}
      {selectedSubCategory && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold mb-2">Параметры для {selectedSubCategory}</h3>
          {Object.entries(eventList[selectedCategory][selectedSubCategory].dto).map(
            ([paramKey, paramVal]) => renderInputField(paramKey, paramVal),
          )}

          {/* Кнопка отправки */}
          <div className="mt-4">
            <Button className="w-full" onClick={handleSubmit}>
              Отправить
            </Button>
          </div>

          {/* Отображение ответа */}
          {response && (
            <div className="mt-4">
              <div>Ответ:</div>
              <pre>{stringifyResponse()}</pre>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default EventListContainer;
